// @ts-nocheck
import Typography from "@store/MobiusOverrides/Typography/Typography";
import { getBudget } from "@insite/client-framework/Services/BudgetService";
import translate from "@insite/client-framework/Translate";
import { BudgetModel } from "@insite/client-framework/Types/ApiModels";
import Select from "@store/MobiusOverrides/Select/Select";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useReportApi, { ApiParameters } from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import { applyTimeZoneOnDate } from "@store/Framework/Common/Utilities/timeZonedDate";
import { BudgetSummaryItemModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { isInvoiceSummaryModel } from "@store/Framework/Services/Reporting/ReportingTypeGuards";
import { CompanyLocation } from "@store/Framework/Store/Bunzl/Pages/Reporting/LoadLocations";
import {
    locationsSelector,
    reportParametersSelector,
} from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import useClear from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useClear";
import { CustomWidgetModule, InvoiceSummaryData } from "@store/Framework/Types/types";
import ReportDataTable, { DataTableData } from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import AccordionLayout from "@store/Library/Components/Reporting/Common/AccordionLayout";
import ApiButtons from "@store/Library/Components/Reporting/Common/ApiButtons";
import ReportContainer from "@store/Library/Components/Reporting/Common/ReportContainer";
import ReportingFilterGridItem from "@store/Library/Components/Reporting/Common/ReportingFilterGridItem";
import ReportingShipToPicker from "@store/Library/Components/Reporting/ShipToPicker/ReportingShipToPicker";
import React, { useState } from "react";
import { useSelector } from "react-redux";

interface BudgetSummaryData {
    name: string;
    startdate: string;
    totalpurchaselimit: number;
    expenditures: number;
    budgetremaining: number;
    shipToName?: string;
    shipToNumber?: string;
    customerName?: string;
}

interface InvoiceTotalByPeriodAndCustomer {
    [customerId: string]: number[];
}

const getNameDataForExcelExport = (customerId: string, companyLocations: CompanyLocation[]) => {
    const location = companyLocations.find(companyLocation =>
        companyLocation.customers.some(customer => customer.id === customerId),
    );
    const customer = location?.customers.find(customer => customer.id === customerId);
    return { location: location?.name, shipTo: customer?.displayName, shipToNumber: customer?.customerSequence };
};
interface BudgetPeriod {
    startDate: Date;
    endDate: Date;
    shipToId: string;
}

type BudgetYear = BudgetPeriod[];

const getBudgetPeriods = (budget: BudgetModel): BudgetYear => {
    const shipToId = budget.shipToId;
    const budgetPeriods =
        budget.budgetLineCollection?.map((budgetLine, index): BudgetPeriod | undefined => {
            if (budget.budgetLineCollection) {
                const startDate = applyTimeZoneOnDate(new Date(budgetLine.startDate));
                const endDate =
                    index !== budget.budgetLineCollection?.length - 1
                        ? new Date(budget.budgetLineCollection?.[index + 1].startDate || 0)
                        : new Date(startDate.getFullYear(), 11, 31);
                return { startDate, endDate, shipToId };
            }
            return undefined;
        }) || [];

    return budgetPeriods.filter(period => period !== undefined) as BudgetYear;
};

const getCustomerById = (id: string, companyLocations: CompanyLocation[]) => {
    const location = companyLocations.find(companyLocation =>
        companyLocation.customers.some(customer => customer.id === id),
    );
    return location?.customers.find(customer => customer.id === id);
};
const getInvoiceTotalByBudgetPeriodAndCustomer = (
    invoiceSummaries: InvoiceSummaryData[],
    customerIdDictionary: Record<string, string>,
    budgetYears: BudgetYear[],
): InvoiceTotalByPeriodAndCustomer => {
    const invoiceTotalsByPeriodAndCustomer = invoiceSummaries.reduce((invoiceSummaryAccumulator, invoiceSummary) => {
        const customerName = invoiceSummary.customername;
        const invoiceDate = new Date(invoiceSummary.invoicedate);
        const id = customerIdDictionary[customerName];
        const budgetYear = budgetYears.find(budgetYear => budgetYear[0].shipToId === id);
        const period = budgetYear?.find(({ startDate, endDate }) => {
            return invoiceDate >= startDate && invoiceDate <= endDate;
        });

        if (!period) {
            return invoiceSummaryAccumulator;
        }
        const periodIndex = budgetYear?.indexOf(period);

        if (!periodIndex && periodIndex !== 0) {
            return invoiceSummaryAccumulator;
        }
        if (!invoiceSummaryAccumulator[id]) {
            invoiceSummaryAccumulator[id] = [];
        }
        if (!invoiceSummaryAccumulator[id][periodIndex]) {
            invoiceSummaryAccumulator[id][periodIndex] = 0;
        }
        invoiceSummaryAccumulator[id][periodIndex] += invoiceSummary.ordertotal;
        return invoiceSummaryAccumulator;
    }, {} as InvoiceTotalByPeriodAndCustomer);
    return invoiceTotalsByPeriodAndCustomer;
};

export const getCustomerIdByNameDictionary = (companyLocations: CompanyLocation[]) => {
    const customerIdDictionary = companyLocations.reduce((acc, curr) => {
        curr.customers.forEach(customer => {
            acc[customer.name] = customer.id;
        });
        return acc;
    }, {} as Record<string, string>);
    return customerIdDictionary;
};

const getFormattedNameById = (id: string, companyLocations: CompanyLocation[]) => {
    const customer = getCustomerById(id, companyLocations);
    return `${customer?.displayName} ${customer?.customerSequence}`;
};
export const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2023; year <= currentYear; year++) {
        years.push(year);
    }
    return years;
};
const budgetSummaryTableHeaders = ["Location", "Start Date", "End Date", "Budget", "Expenditures", "Remaining"];
const budgetSummaryTableKeys: (keyof BudgetSummaryItemModel)[] = [
    "name",
    "startdate",
    "enddate",
    "totalpurchaselimit",
    "expenditures",
    "budgetremaining",
];

const BudgetSummaryReport: React.FC = () => {
    const parameters = useSelector(reportParametersSelector);
    const [budgetYear, setBudgetYear] = useState<number>(new Date().getFullYear());
    const [budgets, setBudgets] = useState<BudgetSummaryData[]>([]);
    const onClear = useClear();
    const {
        updateData: updateInvoiceSummaries,
        isLoading,
        hasSubmitted,
        isError,
        setHasSubmitted,
    } = useReportApi("invoiceSummary");

    const locationData = useSelector(locationsSelector);

    const localizeCurrency = useLocalizeCurrency();
    const handleClear = () => {
        onClear();
        setBudgets([]);
        setHasSubmitted(false);
    };
    const handleDataUpdate = async () => {
        const startOfFiscalYear = new Date(budgetYear, 0, 1).toLocaleDateString();
        const endOfFiscalYear = new Date(budgetYear, 11, 31).toLocaleDateString();
        const customers = parameters.customers;
        const budgetPromises = customers.map(customer =>
            getBudget({ shipToId: customer, fiscalYear: budgetYear, fullGrid: true, userProfileId: "" }),
        );
        const invoiceSummaryPromise = updateInvoiceSummaries({
            ...parameters,
            startDate: startOfFiscalYear,
            endDate: endOfFiscalYear,
        } as ApiParameters);
        const budgetReturns = await Promise.all(budgetPromises);
        const invoiceSummaries = await invoiceSummaryPromise;
        const companyIdsByName = getCustomerIdByNameDictionary(locationData || []);
        const budgetPeriods = budgetReturns.map(budget => getBudgetPeriods(budget));
        if (isInvoiceSummaryModel(invoiceSummaries)) {
            const invoiceTotalsByMonthAndCustomer = getInvoiceTotalByBudgetPeriodAndCustomer(
                invoiceSummaries.items,
                companyIdsByName,
                budgetPeriods,
            );
            const newBudgetData = getTableData(invoiceTotalsByMonthAndCustomer, budgetReturns);
            setBudgets(newBudgetData || []);
        }
    };
    const getTableData = (
        invoiceTotalByMonthAndCustomer: InvoiceTotalByPeriodAndCustomer,
        budgetData: BudgetModel[],
    ): BudgetSummaryData[] => {
        const budgetSummaries = budgetData.map(budget => {
            return (
                budget.budgetLineCollection?.map((budgetLine, index) => {
                    const startDate = applyTimeZoneOnDate(new Date(budgetLine.startDate));
                    const startDateString = startDate.toLocaleDateString();

                    let enddate: Date;
                    if (budget.budgetLineCollection?.[index + 1]?.startDate) {
                        enddate = new Date(budget.budgetLineCollection?.[index + 1]?.startDate);
                        enddate.setDate(enddate.getDate() - 1);
                    } else {
                        enddate = new Date(startDate.getFullYear(), 11, 31);
                    }

                    const endDateString = applyTimeZoneOnDate(enddate).toLocaleDateString();
                    const totalpurchaselimit = budgetLine.currentFiscalYearBudget || 0;
                    const expenditures = invoiceTotalByMonthAndCustomer[budget.shipToId]?.[index] || 0;
                    const budgetremaining = totalpurchaselimit - expenditures;
                    const formattedName = getFormattedNameById(budget.shipToId, locationData || []);
                    const { location, shipTo, shipToNumber } = getNameDataForExcelExport(
                        budget.shipToId,
                        locationData || [],
                    );
                    return {
                        startdate: startDateString,
                        totalpurchaselimit,
                        expenditures,
                        budgetremaining,
                        name: formattedName,
                        enddate: endDateString,
                        shipToName: shipTo,
                        shipToNumber,
                        customerName: location,
                    };
                }) || []
            );
        });

        return ([] as BudgetSummaryData[]).concat(...budgetSummaries);
    };
    const budgetSummaryTableData: DataTableData<any> = {
        data: budgets,
        dataSummaryTitle: translate("Budgets"),
        formatDictionary: {
            totalpurchaselimit: localizeCurrency,
            expenditures: localizeCurrency,
            budgetremaining: localizeCurrency,
        },
        dataKeys: budgetSummaryTableKeys,
        headers: budgetSummaryTableHeaders,
        title: translate("Budgets"),
    };
    const excelData = {
        ...budgetSummaryTableData,
        headers: ["Location", "Ship To", "Ship To Number", ...budgetSummaryTableHeaders.slice(1)],
        dataKeys: ["customerName", "shipToName", "shipToNumber", ...budgetSummaryTableKeys.slice(1)],
    };
    return (
        <ReportContainer>
            <AccordionLayout>
                <ReportingFilterGridItem>
                    <ReportingShipToPicker isError={isError} />
                </ReportingFilterGridItem>
                <ReportingFilterGridItem>
                    <Select
                        value={budgetYear}
                        label={translate("Budget Year")}
                        onChange={event => {
                            setBudgetYear(Number(event.currentTarget.value));
                        }}
                    >
                        {getYears().map(year => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </Select>
                </ReportingFilterGridItem>
                <ApiButtons onClear={handleClear} updateData={handleDataUpdate} isLoading={isLoading} />
            </AccordionLayout>
            {hasSubmitted && budgets.length === 0 && !isLoading && (
                <Typography variant="h3">{translate("No results found")}</Typography>
            )}
            {budgets.length > 0 && <ReportDataTable dataTableData={budgetSummaryTableData} excelData={excelData} />}
        </ReportContainer>
    );
};

const widgetModule: CustomWidgetModule = {
    component: BudgetSummaryReport,
    definition: {
        group: "Reporting",
        displayName: "Budget Summary Report",
    },
};

export default widgetModule;
